// ═══════════════════════════════════════════════════════════════
// OpenGov EMC — useMyOrgData Hooks
// Filtered MainStreet data scoped to the user's organization
// ═══════════════════════════════════════════════════════════════

import { useMemo } from 'react';
import { useMyOrg } from './useMyOrg';
import { useMainStreetStore } from '../store/mainstreetStore';
import { useMainStreetOpsStore } from '../store/mainstreetOpsStore';

/** Facilities belonging to the user's org */
export function useMyFacilities() {
  const myOrg = useMyOrg();
  const facilities = useMainStreetStore(s => s.facilities);

  return useMemo(() => {
    if (!myOrg) return [];
    const orgId = String(myOrg.id);
    return facilities.filter(f => f.orgId === orgId);
  }, [myOrg, facilities]);
}

/** Contacts belonging to the user's org */
export function useMyContacts() {
  const myOrg = useMyOrg();
  const contacts = useMainStreetStore(s => s.contacts);

  return useMemo(() => {
    if (!myOrg) return [];
    const orgId = String(myOrg.id);
    return contacts.filter(c => c.orgId === orgId);
  }, [myOrg, contacts]);
}

/** Damage reports for the user's org */
export function useMyDamageReports() {
  const myOrg = useMyOrg();
  const damageReports = useMainStreetOpsStore(s => s.damageReports);

  return useMemo(() => {
    if (!myOrg) return [];
    const orgId = String(myOrg.id);
    return damageReports.filter(d => d.orgId === orgId);
  }, [myOrg, damageReports]);
}

/** Status check responses for the user's org */
export function useMyStatusResponses() {
  const myOrg = useMyOrg();
  const statusResponses = useMainStreetOpsStore(s => s.statusResponses);

  return useMemo(() => {
    if (!myOrg) return [];
    const orgId = String(myOrg.id);
    return statusResponses.filter(s => s.orgId === orgId);
  }, [myOrg, statusResponses]);
}

/** Re-entry passes for the user's org */
export function useMyReentryPasses() {
  const myOrg = useMyOrg();
  const reentryPasses = useMainStreetOpsStore(s => s.reentryPasses);

  return useMemo(() => {
    if (!myOrg) return [];
    const orgId = String(myOrg.id);
    return reentryPasses.filter(p => p.orgId === orgId);
  }, [myOrg, reentryPasses]);
}

/** Resource offers for the user's org */
export function useMyResourceOffers() {
  const myOrg = useMyOrg();
  const resourceOffers = useMainStreetOpsStore(s => s.resourceOffers);

  return useMemo(() => {
    if (!myOrg) return [];
    const orgId = String(myOrg.id);
    return resourceOffers.filter(r => r.orgId === orgId);
  }, [myOrg, resourceOffers]);
}
